import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

const OrderStatusContext = createContext(null);

const STATUS_MESSAGES = {
  confirmed: { title: 'Order Confirmed!', message: 'Your order has been received and is being prepared.' },
  preparing: { title: 'Preparing Your Order', message: 'Our baristas are crafting your order now.' },
  ready: { title: 'Order Ready!', message: 'Your order is ready for pickup!' },
  completed: { title: 'Thank You!', message: 'Enjoy your order! See you again soon.' },
  cancelled: { title: 'Order Cancelled', message: 'Your order has been cancelled.' }
};

export function OrderStatusProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [orderStatuses, setOrderStatuses] = useState({}); // { orderId: status }
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  
  // Get WebSocket URL from backend URL
  const getWsUrl = useCallback((path) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert https:// to wss:// or http:// to ws://
    const wsProtocol = backendUrl.startsWith('https://') ? 'wss://' : 'ws://';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return `${wsProtocol}${wsHost}${path}`;
  }, []);

  // Connect to user's order updates
  const connectToUserOrders = useCallback((userId) => {
    if (!userId) return;
    
    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = getWsUrl(`/api/ws/user/${userId}`);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected for user orders');
        setIsConnected(true);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 25000);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'order_status_update') {
            const { order_id, status, order } = data;
            
            // Update local state
            setOrderStatuses(prev => ({
              ...prev,
              [order_id]: status
            }));
            setLastUpdate({ order_id, status, order, timestamp: new Date() });
            
            // Show toast notification for important status changes
            if (STATUS_MESSAGES[status]) {
              const { title, message } = STATUS_MESSAGES[status];
              if (status === 'ready') {
                toast.success(title, { description: message, duration: 10000 });
              } else if (status === 'cancelled') {
                toast.error(title, { description: message });
              } else {
                toast.info(title, { description: message });
              }
            }
          }
        } catch (e) {
          // Ignore non-JSON messages (pong, etc.)
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectToUserOrders(userId);
          }, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [getWsUrl]);

  // Connect to a specific order's updates
  const connectToOrder = useCallback((orderId, userId = null) => {
    if (!orderId) return;
    
    const wsUrl = getWsUrl(`/api/ws/orders/${orderId}${userId ? `?user_id=${userId}` : ''}`);
    
    try {
      const orderWs = new WebSocket(wsUrl);
      
      orderWs.onopen = () => {
        console.log(`WebSocket connected for order: ${orderId}`);
      };
      
      orderWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'order_status_update') {
            const { order_id, status, order } = data;
            
            setOrderStatuses(prev => ({
              ...prev,
              [order_id]: status
            }));
            setLastUpdate({ order_id, status, order, timestamp: new Date() });
            
            // Show toast for status updates
            if (STATUS_MESSAGES[status]) {
              const { title, message } = STATUS_MESSAGES[status];
              if (status === 'ready') {
                toast.success(title, { description: message, duration: 10000 });
              } else if (status === 'cancelled') {
                toast.error(title, { description: message });
              } else {
                toast.info(title, { description: message });
              }
            }
          }
        } catch (e) {
          // Ignore non-JSON messages
        }
      };
      
      orderWs.onclose = () => {
        console.log(`WebSocket closed for order: ${orderId}`);
      };
      
      return () => {
        orderWs.close();
      };
    } catch (error) {
      console.error('Failed to create order WebSocket:', error);
    }
  }, [getWsUrl]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    
    setIsConnected(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const value = {
    isConnected,
    orderStatuses,
    lastUpdate,
    connectToUserOrders,
    connectToOrder,
    disconnect,
    getOrderStatus: (orderId) => orderStatuses[orderId]
  };

  return (
    <OrderStatusContext.Provider value={value}>
      {children}
    </OrderStatusContext.Provider>
  );
}

export function useOrderStatus() {
  const context = useContext(OrderStatusContext);
  if (!context) {
    throw new Error('useOrderStatus must be used within an OrderStatusProvider');
  }
  return context;
}

export default OrderStatusContext;
